// src/shared/components/MarkdownField/MarkdownField.tsx

import { useState, useRef, type ChangeEvent } from 'react';
import { Input, Textarea, Box, Text } from '@mantine/core';
import { MarkdownToolbar } from './MarkdownToolbar';
import { MarkdownPreview } from './MarkdownPreview';
import type { InsertResult } from '@/shared/utils/MarkdownInsert';

type Mode = 'write' | 'preview';

interface MarkdownFieldProps {
  value: string;
  onChange: (value: string | ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur: () => void;
  label: string;
  maxLength: number;
  description?: string;
  error?: string;
  rows?: number;
  placeholder?: string;
}

/**
 * Form field for editing Markdown content with Write/Preview toggle.
 * Props mirror Mantine Textarea for drop-in replacement compatibility.
 */
export function MarkdownField({
  value,
  onChange,
  onBlur,
  label,
  maxLength,
  description,
  error,
  rows = 6,
  placeholder,
}: MarkdownFieldProps) {
  const [mode, setMode] = useState<Mode>('write');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    // Auto-focus textarea when switching back to write mode
    if (newMode === 'write') {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  };

  const handleInsert = (result: InsertResult) => {
    // Update the value via onChange
    onChange(result.value);

    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
      }
    });
  };

  // Calculate min height based on rows (approximate line height)
  const minHeight = rows * 24 + 16; // 24px per line + padding

  return (
    <Input.Wrapper
      label={label}
      description={description}
      error={error}
    >
      <MarkdownToolbar
        mode={mode}
        onModeChange={handleModeChange}
        textareaRef={textareaRef}
        value={value}
        onInsert={handleInsert}
      />

      {mode === 'write' ? (
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          maxLength={maxLength}
          rows={rows}
          placeholder={placeholder}
          styles={{
            input: {
              fontFamily: 'var(--mantine-font-family-monospace)',
              fontSize: 'var(--mantine-font-size-sm)',
            },
          }}
        />
      ) : (
        <Box
          style={{
            minHeight,
            maxHeight: 350,
            overflowY: 'auto',
            padding: 'var(--mantine-spacing-sm)',
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 'var(--mantine-radius-sm)',
            backgroundColor: 'var(--mantine-color-gray-0)',
          }}
        >
          {value.trim() ? (
            <MarkdownPreview source={value} />
          ) : (
            <Text c="dimmed" size="sm" fs="italic">
              Nothing to preview
            </Text>
          )}
        </Box>
      )}
    </Input.Wrapper>
  );
}
